// frontend/src/components/common/Banner.js
import React, { useState, useEffect } from 'react';
import { bannersApi } from '../../api/banners';
import { FaTimes, FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function Banner() {
  const [banner, setBanner] = useState(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchActiveBanner = async () => {
      try {
        const data = await bannersApi.getActiveBanner();
        
        // Check if data exists and is explicitly active
        if (data && data.is_active) {
            setBanner(data);
            setVisible(true);
        }
      } catch (err) {
        console.error("❌ [Banner] API Error:", err);
      }
    };
    fetchActiveBanner();
  }, []);

  if (!banner || !visible) return null;

  const handleBannerClick = () => {
      if (banner.link_url) {
          navigate(banner.link_url);
      }
  };

  // Font Size Mapping
  const fontSizeClass = {
      small: 'text-xs',
      medium: 'text-sm md:text-base',
      large: 'text-lg',
      xlarge: 'text-xl font-bold'
  }[banner.font_size || 'medium'];

  // Animation Settings
  const isSlide = banner.animation_mode === 'slide';
  const speed = banner.animation_speed || 15; // Seconds for one full cycle

  return (
    <div 
        className="relative z-[60] overflow-hidden shadow-md w-full transition-all duration-300"
        style={{ 
            backgroundColor: banner.background_color || '#2563EB', 
            color: banner.text_color || '#FFFFFF',
            height: '40px', 
            display: 'flex',
            alignItems: 'center'
        }}
    >
      {/* --- TEXT CONTAINER --- */}
      <div 
        className="flex-grow overflow-hidden relative h-full flex items-center cursor-pointer"
        onClick={handleBannerClick}
      >
          {isSlide ? (
              // SLIDING TRACK (Right to Left)
              <div 
                className="flex whitespace-nowrap absolute"
                style={{
                    animation: `marquee ${speed}s linear infinite`,
                    willChange: 'transform',
                    minWidth: '100%'
                }}
              >
                  {/* Repeat text multiple times to ensure continuous flow */}
                  {[...Array(6)].map((_, i) => (
                      <span key={i} className={`font-bold ${fontSizeClass} px-12`}>
                          {banner.text}
                      </span>
                  ))}
              </div>
          ) : (
              // STATIC TEXT
              <div className={`w-full text-center font-bold ${fontSizeClass} px-4 truncate`}>
                  {banner.text}
              </div>
          )}
      </div>

      {/* --- STATIC CONTROLS (Right Side) --- */}
      <div 
        className="flex items-center px-4 h-full z-20 shadow-[-5px_0_10px_rgba(0,0,0,0.1)]"
        style={{ backgroundColor: banner.background_color || '#2563EB' }}
      >
          {banner.link_url && (
              <span 
                className="text-xs bg-white/20 px-2 py-1 rounded hidden sm:flex items-center gap-1 hover:bg-white/30 transition cursor-pointer mr-2 whitespace-nowrap"
                onClick={handleBannerClick}
              >
                  View <FaArrowRight size={10} />
              </span>
          )}
          <button 
            onClick={(e) => {
                e.stopPropagation();
                setVisible(false);
            }}
            className="p-1 hover:bg-black/10 rounded-full transition"
            aria-label="Close Banner"
          >
            <FaTimes />
          </button>
      </div>
      
      {/* --- CSS ANIMATION KEYFRAMES --- */}
      <style>{`
        @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); } 
        }
      `}</style>
    </div>
  );
}

export default Banner;